import { Spacing, Typography } from '@persona-id/design-tokens';
import { useCallback } from 'react';
import styled from 'styled-components';

import { StyleLabel } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs/StyleInput';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Paragraph } from 'design-system/components/Text';
import ToggleGroup, { ToggleGroupItem } from 'design-system/components/ToggleGroup/ToggleGroup';

const ToggleStyleLabel = styled(StyleLabel)`
  margin-bottom: 0;
`;

const ToggleStyleLabelContainer = styled.div`
  display: flex;
  margin-bottom: ${Spacing[1]};
  align-items: end;
`;

const StyledToggleGroupItem = styled(ToggleGroupItem)`
  height: 24px;
  padding-top: ${Spacing[1]};
  padding-right: ${Spacing[2]};
  padding-bottom: ${Spacing[1]};
  padding-left: ${Spacing[2]};
  ${Typography.TextSmall};

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const StyledHelpIcon = styled(HelpIcon)`
  width: 20px;
  height: 20px;
  display: inline-flex;
  justify-content: center;
  align-items: flex-end;
`;

type ToggleStyleInputProps = {
  field: string;
  name: string;
  options: { label: JSX.Element | string; value: string }[];
  defaultValue: string | undefined;
  label?: string;
  labelTooltip?: string;
  editable?: boolean;
  onChange: (value: string, field: string) => void;
};

const ToggleStyleInput = ({
  field,
  name,
  options,
  defaultValue,
  label,
  labelTooltip,
  editable,
  onChange,
}: ToggleStyleInputProps) => {
  const labelId = `${field}-${name}`;

  const handleOnChange = useCallback(
    (value: string) => {
      onChange(value, field);
    },
    [onChange, field],
  );

  const LabelElement =
    label != null ? (
      <ToggleStyleLabelContainer>
        <ToggleStyleLabel id={labelId}>{label}</ToggleStyleLabel>
        {labelTooltip != null && (
          <StyledHelpIcon>
            <Paragraph size='sm'>{labelTooltip}</Paragraph>
          </StyledHelpIcon>
        )}
      </ToggleStyleLabelContainer>
    ) : null;

  return (
    <>
      {LabelElement}
      <ToggleGroup
        type='single'
        key={`toggle-group-single-${field}-${defaultValue}`}
        defaultValue={defaultValue}
        fullWidth
        {...(label != null && { 'aria-labelledby': labelId })}
        disabled={!editable}
        onValueChange={handleOnChange}
      >
        {options?.map(({ label, value }) => (
          <StyledToggleGroupItem
            key={value}
            value={value}
            // Note (Evan): For our use case with styles, we don't want to
            // unselect toggle items and have values unset
            id={`toggle-group-${field}__item-${value}`}
            onClick={(e) => {
              if (value === defaultValue) {
                e.preventDefault();
                return false;
              }
            }}
          >
            <div>{label}</div>
          </StyledToggleGroupItem>
        ))}
      </ToggleGroup>
    </>
  );
};

export default ToggleStyleInput;
